import React, { useEffect, useMemo, useState } from "react";
import { Bell, BellOff, Clock, MapPin, Thermometer, Wind, AlertTriangle } from "lucide-react";
import { toast } from "./ui/sonner";
import { disablePush, enablePush, getPushState } from "../utils/push";
import { fetchNWSActive } from "../utils/nws";

// Localized clock with device timezone
const useLocalClock = () => {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  return now;
};

// Try to detect coarse geolocation for local conditions
const useLocalConditions = () => {
  const [state, setState] = useState({ place: null, temp: null, wind: null, code: null });
  useEffect(() => {
    let cancelled = false;
    const load = async (lat, lon, place) => {
      try {
        const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/weather/forecast?lat=${lat}&lon=${lon}`);
        const j = await r.json();
        if (cancelled) return;
        setState({ place, temp: j.current?.temp_f, wind: j.current?.wind_mph, code: j.current?.code });
      } catch(_) {}
    };
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/weather/iplocate`).then(r => r.ok ? r.json() : null).then(d => {
      if (d && d.latitude && d.longitude) load(d.latitude, d.longitude, `${d.name}, ${d.admin1}`);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);
  return state;
};

// Real web push subscription (works with the tab closed once granted)
const usePushAlerts = () => {
  const [state, setState] = useState("checking");
  const [busy, setBusy] = useState(false);

  useEffect(() => { getPushState().then(setState); }, []);

  const request = async () => {
    setBusy(true);
    try {
      if (state === "subscribed") {
        await disablePush();
        setState("default");
        toast.success("Push alerts turned off");
      } else {
        const result = await enablePush();
        setState("subscribed");
        toast.success(result?.state ? `Alerts on for ${result.state}` : "Alerts on for major warnings");
      }
    } catch (error) {
      toast.error(error.message || "Could not enable push alerts");
    } finally {
      setBusy(false);
    }
  };

  return { supported: state !== "unsupported", state, busy, request };
};

const codeLabel = (c) => {
  const m = { 0:"Clear",1:"Mostly clear",2:"Partly cloudy",3:"Overcast",45:"Fog",51:"Drizzle",61:"Rain",71:"Snow",80:"Showers",95:"T-storm" };
  return m[c] ?? "—";
};

const BottomChyron = () => {
  const now = useLocalClock();
  const cond = useLocalConditions();
  const [liveAlerts, setLiveAlerts] = useState([]);
  useEffect(() => {
    let cancel = false;
    const load = async () => {
      const data = await fetchNWSActive();
      if (!cancel) setLiveAlerts(data.slice(0, 12));
    };
    load();
    const t = setInterval(load, 60000);
    return () => { cancel = true; clearInterval(t); };
  }, []);
  const { supported, state: pushState, busy: pushBusy, request } = usePushAlerts();

  const tz = useMemo(() => {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch(_) { return "UTC"; }
  }, []);
  const timeStr = now.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  const dateStr = now.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });

  // Build ticker text from LIVE NWS data
  const ticker = useMemo(() => {
    const parts = [];
    liveAlerts.forEach(a => {
      let exp = "";
      try { exp = a.expires ? new Date(a.expires).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""; } catch(_) {}
      parts.push(`\u26a0 ${a.event} \u2022 ${a.areaDesc}${exp ? " \u2022 expires " + exp : ""}`);
    });
    if (liveAlerts.length === 0) parts.push("ALL CLEAR \u2022 No active NWS watches or warnings");
    if (cond.temp != null) parts.push(`\u25cf ${cond.place || "Local"} ${Math.round(cond.temp)}\u00b0F \u2022 wind ${Math.round(cond.wind||0)} mph`);
    parts.push("Storm Desk Live Weather \u2022 24/7 severe weather coverage");
    return parts.join("    \u25c6    ");
  }, [cond, liveAlerts]);

  return (
    <div data-testid="bottom-live-chyron" className="fixed bottom-0 inset-x-0 z-40 border-t border-white/10 bg-[rgba(5,7,13,0.94)] backdrop-blur-md">
      {/* Scrolling ticker */}
      <div className="h-7 border-b border-white/5 flex items-center overflow-hidden text-[11px]">
        <div className="px-3 h-full flex items-center gap-1.5 bg-red-600 text-white font-bold uppercase tracking-widest mono">
          <AlertTriangle className="w-3 h-3" /> Live
        </div>
        <div className="relative flex-1 overflow-hidden">
          <div className="flex whitespace-nowrap marquee-track gap-8 pl-6 mono text-slate-200">
            <span>{ticker}</span>
            <span aria-hidden>{ticker}</span>
          </div>
        </div>
      </div>
      {/* Status bar */}
      <div className="h-8 px-2 sm:px-3 flex items-center gap-3 text-[11px] mono text-slate-300">
        <div data-testid="bottom-live-clock" className="flex shrink-0 items-center gap-1.5 border-r border-white/10 pr-3">
          <Clock className="w-3.5 h-3.5 text-cyan-400" />
          <span className="text-white font-bold tabular-nums">{timeStr}</span>
          <span className="text-slate-500 hidden sm:inline">{dateStr}</span>
          <span className="text-slate-500 hidden md:inline">{"·"} {tz}</span>
        </div>
        {cond.place && (
          <div className="hidden sm:flex items-center gap-1.5">
            <MapPin className="w-3.5 h-3.5 text-red-400" /> {cond.place}
          </div>
        )}
        {cond.temp != null && (
          <>
            <div className="hidden md:flex items-center gap-1.5"><Thermometer className="w-3.5 h-3.5 text-amber-400" />{Math.round(cond.temp)}{"°"}F</div>
            <div className="hidden md:flex items-center gap-1.5"><Wind className="w-3.5 h-3.5 text-cyan-300" />{Math.round(cond.wind||0)} mph</div>
          </>
        )}
        <div className="flex-1" />
        {supported ? (
          pushState === "subscribed" ? (
            <button onClick={request} disabled={pushBusy} data-testid="disable-push-alerts-button" className="flex items-center gap-1.5 rounded px-2 py-1 text-emerald-300 transition-colors hover:bg-white/10">
              <Bell className="w-3.5 h-3.5" /> Alerts on
            </button>
          ) : (
            <button onClick={request} disabled={pushBusy} data-testid="enable-push-alerts-button" className="flex items-center gap-1.5 rounded bg-red-600 px-2 py-1 text-white transition-colors hover:bg-red-500 disabled:opacity-60">
              <Bell className="w-3.5 h-3.5" /> {pushBusy ? "Enabling…" : "Enable push alerts"}
            </button>
          )
        ) : (
          <div className="flex items-center gap-1.5 text-slate-500"><BellOff className="w-3.5 h-3.5" /> Push unsupported</div>
        )}
      </div>
    </div>
  );
};

export default BottomChyron;
